import AnnouncementPageId from "@mapmycustomers/shared/enum/announcements/AnnouncementPageId";

import Path from "@app/enum/Path";
import ReportsMenuOptions from "@app/enum/ReportsMenuOptions";
import RouteSubPath from "@app/enum/RouteSubPath";
import SettingPath from "@app/enum/settings/SettingPath";

export interface AnnouncementPage {
  navigablePath?: string; // absent: the page cannot be opened directly, so a CTA cannot target it
  patterns: string[];
}

const openablePage = (path: string): AnnouncementPage => ({
  navigablePath: path,
  patterns: [path],
});

const settingsPage = (settingPath: SettingPath): AnnouncementPage =>
  openablePage(`${Path.SETTINGS}/${settingPath}`);

// The view on its own and under the user, team and team-user scopes.
const dashboardViewPatterns = (view: string): string[] => [
  `${Path.DASHBOARD}/${view}`,
  `${Path.DASHBOARD}/users/:userId/${view}`,
  `${Path.DASHBOARD}/teams/:teamId/${view}`,
  `${Path.DASHBOARD}/teams/:teamId/users/:userId/${view}`,
];

// Patterns are react-router prefixes based on the mounted routes, with required params so a
// list page never resolves to its record page. Signed-out routes, "/", /web1 and /sso-access are
// deliberately absent: an announcement never shows on them.
export const ANNOUNCEMENT_PAGES: Record<AnnouncementPageId, AnnouncementPage> = {
  [AnnouncementPageId.ACTIVITIES]: openablePage(Path.ACTIVITY),
  [AnnouncementPageId.ACTIVITIES_CALENDAR]: openablePage(`${Path.ACTIVITY}/calendar`),
  [AnnouncementPageId.ACTIVITIES_LIST]: openablePage(`${Path.ACTIVITY}/list`),
  [AnnouncementPageId.ACTIVITIES_RECORD]: {
    patterns: [`${Path.ACTIVITY}/list/:aid`, `${Path.ACTIVITY}/:aid/files/:fileId`],
  },
  [AnnouncementPageId.ACTIVITIES_SCHEDULER]: openablePage(`${Path.ACTIVITY}/scheduler`),
  [AnnouncementPageId.BILLING]: openablePage(Path.BILLING),
  [AnnouncementPageId.COMPANIES]: openablePage(Path.COMPANY),
  [AnnouncementPageId.COMPANIES_RECORD]: { patterns: [`${Path.COMPANY}/:cid`] },
  [AnnouncementPageId.DASHBOARD]: openablePage(Path.DASHBOARD),
  [AnnouncementPageId.DASHBOARD_COACHING]: {
    navigablePath: `${Path.DASHBOARD}/coaching`,
    patterns: dashboardViewPatterns("coaching"),
  },
  [AnnouncementPageId.DASHBOARD_EXECUTIVE_SUMMARY]: {
    navigablePath: `${Path.DASHBOARD}/executive-summary`,
    patterns: dashboardViewPatterns("executive-summary"),
  },
  [AnnouncementPageId.DASHBOARD_TASKS]: { patterns: dashboardViewPatterns("tasks") },
  [AnnouncementPageId.DASHBOARD_TIMELINE]: {
    navigablePath: `${Path.DASHBOARD}/timeline`,
    patterns: dashboardViewPatterns("timeline"),
  },
  [AnnouncementPageId.DEALS]: openablePage(Path.DEAL),
  [AnnouncementPageId.DEALS_FORECAST]: openablePage(`${Path.DEAL}/forecast`),
  [AnnouncementPageId.DEALS_KANBAN]: openablePage(`${Path.DEAL}/kanban`),
  [AnnouncementPageId.DEALS_LIST]: openablePage(`${Path.DEAL}/list`),
  // Any view mode: the record pane opens beside whichever deals view is showing.
  [AnnouncementPageId.DEALS_RECORD]: {
    patterns: [`${Path.DEAL}/:viewMode/:did`, `${Path.DEAL}/:did/files/:fileId`],
  },
  [AnnouncementPageId.EMAIL_OUTBOX]: openablePage(Path.EMAIL_OUTBOX),
  [AnnouncementPageId.FILES]: { patterns: [`${Path.FILES}/:fileId`] },
  [AnnouncementPageId.MAP]: openablePage(Path.MAP),
  [AnnouncementPageId.MAP_GROUPS]: { patterns: [`${Path.MAP}/groups`] },
  [AnnouncementPageId.MAP_LEADS]: openablePage(`${Path.MAP}/leads`),
  [AnnouncementPageId.MAP_TERRITORIES]: { patterns: [`${Path.MAP}/territories`] },
  [AnnouncementPageId.NOTIFICATIONS]: openablePage(Path.NOTIFICATIONS),
  [AnnouncementPageId.ONBOARDING]: openablePage(Path.ONBOARDING),
  [AnnouncementPageId.ONBOARDING_GUIDES]: openablePage(`${Path.ONBOARDING}/guides`),
  [AnnouncementPageId.ONBOARDING_MY]: openablePage(`${Path.ONBOARDING}/my`),
  [AnnouncementPageId.ONBOARDING_USERS]: openablePage(`${Path.ONBOARDING}/users`),
  [AnnouncementPageId.PEOPLE]: openablePage(Path.PEOPLE),
  [AnnouncementPageId.PEOPLE_RECORD]: { patterns: [`${Path.PEOPLE}/:pid`] },
  // Bare /reports renders an empty body, so the section opens the overview report instead.
  [AnnouncementPageId.REPORTS]: {
    navigablePath: `${Path.REPORTS}/${ReportsMenuOptions.OVERVIEW}`,
    patterns: [Path.REPORTS],
  },
  [AnnouncementPageId.REPORTS_ACTIVITY]: openablePage(`${Path.REPORTS}/activity`),
  [AnnouncementPageId.REPORTS_DASHBOARDS]: {
    navigablePath: `${Path.REPORTS}/${ReportsMenuOptions.ALL_DASHBOARDS}`,
    patterns: [`${Path.REPORTS}/dashboard`, `${Path.REPORTS}/${ReportsMenuOptions.ALL_DASHBOARDS}`],
  },
  [AnnouncementPageId.REPORTS_DEALS]: openablePage(`${Path.REPORTS}/deals`),
  [AnnouncementPageId.REPORTS_EMAIL_ACTIVITY]: openablePage(`${Path.REPORTS}/email-activity`),
  // A prefix, so it also covers creating and editing an export.
  [AnnouncementPageId.REPORTS_EXPORTS]: openablePage(`${Path.REPORTS}/exports`),
  [AnnouncementPageId.REPORTS_MILEAGE_TRACKING]: openablePage(`${Path.REPORTS}/mileage-tracking`),
  [AnnouncementPageId.REPORTS_OVERVIEW]: openablePage(
    `${Path.REPORTS}/${ReportsMenuOptions.OVERVIEW}`
  ),
  [AnnouncementPageId.REPORTS_REPORT_CARD]: openablePage(`${Path.REPORTS}/report-card`),
  [AnnouncementPageId.REPORTS_STACK_RANK]: openablePage(`${Path.REPORTS}/stack-rank`),
  [AnnouncementPageId.RESOURCES]: openablePage(Path.RESOURCES),
  [AnnouncementPageId.ROUTES]: openablePage(Path.ROUTE),
  [AnnouncementPageId.ROUTES_EDIT]: {
    navigablePath: `${Path.ROUTE}/${RouteSubPath.CREATE}`,
    patterns: [
      `${Path.ROUTE}/${RouteSubPath.EDIT}/:routeId`,
      `${Path.ROUTE}/${RouteSubPath.CREATE}`,
    ],
  },
  [AnnouncementPageId.ROUTES_LIST]: openablePage(`${Path.ROUTE}/${RouteSubPath.LIST}`),
  [AnnouncementPageId.ROUTES_UPCOMING]: openablePage(`${Path.ROUTE}/${RouteSubPath.UPCOMING}`),
  [AnnouncementPageId.ROUTES_VIEW]: {
    patterns: [`${Path.ROUTE}/${RouteSubPath.VIEW}/:routeId`],
  },
  [AnnouncementPageId.SETTINGS]: openablePage(Path.SETTINGS),
  // The data, forms and privacy sections have no screen of their own; the pattern puts their
  // pages' URLs under them.
  [AnnouncementPageId.SETTINGS_DATA]: { patterns: [`${Path.SETTINGS}/data`] },
  [AnnouncementPageId.SETTINGS_DATA_IMPORT_HISTORY]: settingsPage(SettingPath.DATA_IMPORT_HISTORY),
  [AnnouncementPageId.SETTINGS_DATA_NEW_IMPORT]: settingsPage(SettingPath.DATA_NEW_IMPORT),
  [AnnouncementPageId.SETTINGS_DATA_RESTORABLE_RECORDS]: settingsPage(
    SettingPath.DATA_RESTORABLE_RECORDS
  ),
  [AnnouncementPageId.SETTINGS_FORMS]: { patterns: [`${Path.SETTINGS}/forms`] },
  [AnnouncementPageId.SETTINGS_FORMS_CREATE_FORMS]: settingsPage(SettingPath.FORMS_CREATE),
  [AnnouncementPageId.SETTINGS_FORMS_FILE_TAGS]: settingsPage(SettingPath.FORMS_FILE_TAGS),
  [AnnouncementPageId.SETTINGS_FORMS_MANAGE_FIELDS]: settingsPage(SettingPath.FORMS_MANAGE_FIELDS),
  [AnnouncementPageId.SETTINGS_FORMS_MERGE_DUPLICATES]: settingsPage(
    SettingPath.FORMS_MERGE_DUPLICATES
  ),
  [AnnouncementPageId.SETTINGS_FORMS_PINNED_FIELDS]: settingsPage(SettingPath.PINNED_FIELDS),
  [AnnouncementPageId.SETTINGS_ORGANIZATION]: { patterns: [`${Path.SETTINGS}/organization`] },
  [AnnouncementPageId.SETTINGS_ORGANIZATION_ACTIVITY_TYPES]: settingsPage(
    SettingPath.ORGANIZATION_ACTIVITY_TYPES
  ),
  [AnnouncementPageId.SETTINGS_ORGANIZATION_DETAILS]: settingsPage(
    SettingPath.ORGANIZATION_DETAILS
  ),
  [AnnouncementPageId.SETTINGS_ORGANIZATION_FEATURES]: settingsPage(
    SettingPath.ORGANIZATION_FEATURES
  ),
  [AnnouncementPageId.SETTINGS_ORGANIZATION_INTEGRATIONS]: settingsPage(
    SettingPath.ORGANIZATION_INTEGRATIONS
  ),
  [AnnouncementPageId.SETTINGS_ORGANIZATION_SANDBOX]: settingsPage(
    SettingPath.ORGANIZATION_SANDBOX
  ),
  [AnnouncementPageId.SETTINGS_PERSONAL]: {
    navigablePath: `${Path.SETTINGS}/${SettingPath.PERSONAL_PROFILE}`,
    patterns: [`${Path.SETTINGS}/personal`],
  },
  [AnnouncementPageId.SETTINGS_PERSONAL_CALENDAR_SYNC]: settingsPage(
    SettingPath.PERSONAL_CALENDAR_SYNC
  ),
  [AnnouncementPageId.SETTINGS_PERSONAL_EMAIL_PREFERENCES]: settingsPage(
    SettingPath.PERSONAL_EMAIL_PREFERENCES
  ),
  [AnnouncementPageId.SETTINGS_PERSONAL_PROFILE]: settingsPage(SettingPath.PERSONAL_PROFILE),
  [AnnouncementPageId.SETTINGS_PERSONAL_SESSIONS]: settingsPage(SettingPath.PERSONAL_SESSIONS),
  [AnnouncementPageId.SETTINGS_PRIVACY]: { patterns: [`${Path.SETTINGS}/privacy`] },
  [AnnouncementPageId.SETTINGS_PRIVACY_NOTIFICATIONS]: settingsPage(
    SettingPath.PRIVACY_NOTIFICATIONS
  ),
};
